import React from 'react';
import {
    ThreadCollection,
    ResearchCard,
    PublicationList,
    TopicTags
} from '../components/researcher';
import '../themes/researcher.css';

// ============================================
// Type Definitions
// ============================================
interface Thread {
    id: string;
    title: string;
    preview: string;
    category: string;
    date: Date;
    likes: number;
    retweets: number;
    link: string;
}

interface ResearchItem {
    id: string;
    title: string;
    excerpt: string;
    category: string;
    image?: string;
    link: string;
}

interface Publication {
    title: string;
    source: string;
    icon: string;
    link: string;
}

interface Stat {
    value: string;
    label: string;
}

export interface ResearcherTemplateData {
    identity: {
        name: string;
        handle: string;
        avatar: string;
        bio: string;
    };
    topics: string[];
    stats: Stat[];
    threads: Thread[];
    deepDives: ResearchItem[];
    publications: Publication[];
    newsletter?: {
        title: string;
        description: string;
        buttonText?: string;
    };
    social: {
        twitter?: string;
        substack?: string;
        linkedin?: string;
        github?: string;
    };
}

// ============================================
// Social Icons Component
// ============================================
const SocialIcon: React.FC<{ platform: string; href: string }> = ({ platform, href }) => {
    const icons: Record<string, string> = {
        twitter: '𝕏',
        substack: '📰',
        linkedin: '💼',
        github: '🐙',
    };

    return (
        <a href={href} target="_blank" rel="noopener noreferrer" title={platform}>
            {icons[platform] || '🔗'}
        </a>
    );
};

// ============================================
// Main Template Component
// ============================================
export const ResearcherTemplate: React.FC<{ data: ResearcherTemplateData }> = ({ data }) => {
    const { identity, topics, stats, threads, deepDives, publications, newsletter, social } = data;

    return (
        <div className="researcher-layout" data-theme="researcher">
            {/* Header */}
            <header className="researcher-header">
                <div className="researcher-header-logo">
                    <img src={identity.avatar} alt={identity.name} />
                    <span>{identity.name}</span>
                </div>

                <nav className="researcher-header-nav">
                    <a href="#threads">Threads</a>
                    <a href="#research">Research</a>
                    <a href="#publications">Publications</a>
                </nav>

                <a
                    href={social.twitter || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="researcher-header-cta"
                >
                    Follow
                </a>
            </header>

            {/* Hero Section */}
            <section className="researcher-hero">
                <div className="researcher-hero-intro">
                    <img
                        src={identity.avatar}
                        alt={identity.name}
                        className="researcher-hero-avatar"
                    />
                    <div className="researcher-hero-info">
                        <h1 className="researcher-hero-name">{identity.name}</h1>
                        <div className="researcher-hero-handle">{identity.handle}</div>
                        <p className="researcher-hero-bio">{identity.bio}</p>
                    </div>
                </div>

                {/* Topic Tags */}
                <TopicTags topics={topics} />

                {/* Stats */}
                {stats.length > 0 && (
                    <div className="researcher-stats">
                        {stats.map((stat, index) => (
                            <div key={index} className="researcher-stat">
                                <div className="researcher-stat-value">{stat.value}</div>
                                <div className="researcher-stat-label">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Threads Section */}
            {threads.length > 0 && (
                <section id="threads" className="researcher-section">
                    <h2 className="researcher-section-title">Popular Threads</h2>
                    <ThreadCollection threads={threads} />
                </section>
            )}

            {/* Deep Dives */}
            {deepDives.length > 0 && (
                <section id="research" className="researcher-section">
                    <h2 className="researcher-section-title">Deep Dives</h2>
                    <ResearchCard items={deepDives} />
                </section>
            )}

            {/* Publications */}
            {publications.length > 0 && (
                <section id="publications" className="researcher-section">
                    <h2 className="researcher-section-title">Publications</h2>
                    <PublicationList publications={publications} />
                </section>
            )}

            {/* Newsletter */}
            {newsletter && (
                <section className="researcher-section">
                    <div className="researcher-newsletter">
                        <h3 className="researcher-newsletter-title">{newsletter.title}</h3>
                        <p className="researcher-newsletter-desc">{newsletter.description}</p>
                        <form className="researcher-newsletter-form" onSubmit={(e) => e.preventDefault()}>
                            <input
                                type="email"
                                className="researcher-newsletter-input"
                                placeholder="you@email.com"
                            />
                            <button type="submit" className="researcher-newsletter-btn">
                                {newsletter.buttonText || 'Subscribe'}
                            </button>
                        </form>
                    </div>
                </section>
            )}

            {/* Footer */}
            <footer className="researcher-footer">
                <div className="researcher-footer-social">
                    {social.twitter && <SocialIcon platform="twitter" href={social.twitter} />}
                    {social.substack && <SocialIcon platform="substack" href={social.substack} />}
                    {social.linkedin && <SocialIcon platform="linkedin" href={social.linkedin} />}
                    {social.github && <SocialIcon platform="github" href={social.github} />}
                </div>
                <p className="researcher-footer-text">
                    © {new Date().getFullYear()} {identity.name} • Built with Crypto Factory
                </p>
            </footer>
        </div>
    );
};

export default ResearcherTemplate;
